import { BaseApi } from '../apiBase';

export interface PortalEvent {
  id: number;
  slug: string;
  name: string;
  description?: string;
  startDate: string;
  endDate: string;
  startTime?: string;
  endTime?: string;
  timeZone?: string;
  location?: {
    id?: number;
    name?: string;
    address1?: string;
    address2?: string;
    city?: string;
    state?: string;
    zip?: string;
    country?: string;
  };
  imageUrl?: string;
  published?: boolean;
  archived?: boolean;
  isRegistered?: boolean;
  team?: {
    id: number;
    name: string;
  };
  keywords?: {
    id: string;
    name: string;
    displayName: string;
  }[];
}

export interface EventAccessCheckResponse {
  hasAccess: boolean;
  requiresLogin: boolean;
  message?: string;
  redirectUrl?: string;
  requiredAccountTypes?: string[];
  requiredDesignations?: string[];
  showCustomMessage?: boolean;
}

interface EventsParams {
  search?: string;
  scope?: 'all' | 'registered';
  page?: number;
  per_page?: number;
  sort?: 'date' | 'name';
  sort_direction?: 'asc' | 'desc';
  keyword_ids?: string[];
}

interface EventsResponse {
  events: PortalEvent[];
  meta: {
    current_page: number;
    next_page: number | null;
    prev_page: number | null;
    total_pages: number;
    total_count: number;
  };
}

class EventsApi extends BaseApi {
  constructor() {
    super(); // Use default /api/portal base URL
  }

  async getEvents(params?: EventsParams): Promise<EventsResponse> {
    const queryParams: Record<string, any> = {};

    if (params) {
      if (params.search) queryParams.search = params.search;
      if (params.scope) queryParams.scope = params.scope;
      if (params.sort) queryParams.sort = params.sort;
      if (params.sort_direction) queryParams.sort_direction = params.sort_direction;
      if (params.page) queryParams.page = params.page;
      if (params.per_page) queryParams.per_page = params.per_page;
      if (params.keyword_ids && params.keyword_ids.length > 0) {
        queryParams.keyword_ids = params.keyword_ids.join(',');
      }
    }

    // Use getSimpleJson to handle RABL response format
    const response = await this.getSimpleJson<any>('/account/events', queryParams);

    // Handle RABL response format - if paginated, it will have events and meta
    // If not paginated, it will be just an array
    let result: EventsResponse;

    if (Array.isArray(response)) {
      result = {
        events: response,
        meta: {
          current_page: 1,
          next_page: null,
          prev_page: null,
          total_pages: 1,
          total_count: response.length
        }
      };
    } else if (response && response.events) {
      // Paginated response with metadata
      // Handle both snake_case and camelCase metadata
      const meta = response.meta || {};
      result = {
        events: response.events || [],
        meta: {
          current_page: meta.current_page || meta.currentPage || 1,
          next_page: meta.next_page || meta.nextPage || null,
          prev_page: meta.prev_page || meta.prevPage || null,
          total_pages: meta.total_pages || meta.totalPages || 1,
          total_count: meta.total_count || meta.totalCount || (response.events || []).length
        }
      };
    } else {
      // Empty or unexpected format
      result = {
        events: [],
        meta: {
          current_page: 1,
          next_page: null,
          prev_page: null,
          total_pages: 1,
          total_count: 0
        }
      };
    }

    return result;
  }

  async getEvent(id: string): Promise<PortalEvent> {
    return this.getSimpleJson<PortalEvent>(`/account/events/${id}`);
  }

  async checkEventAccess(eventSlug: string): Promise<EventAccessCheckResponse> {
    try {
      // Use the standard get method which properly deserializes JSON:API responses
      return this.get<EventAccessCheckResponse>(`/events/${eventSlug}/access_check`);
    } catch (error: any) {
      // If the request fails with a 403, it means access is denied
      if (error.status === 403 && error.data) {
        return error.data;
      }
      // For other errors, assume access is granted (backward compatibility)
      return { hasAccess: true, requiresLogin: false };
    }
  }
}

export const eventsApi = new EventsApi();